
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Reveal } from './Reveal';
import { FileText, Database, Files, Mail, ShieldCheck, Activity } from 'lucide-react';

export const IntegrationSection: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section className="py-20 md:py-40 bg-rabbit-navy relative overflow-hidden border-y border-gray-800">
      {/* Background Ambient Glow */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 right-0 w-[800px] h-[800px] bg-blue-900/10 rounded-full blur-[120px] translate-x-1/2 -translate-y-1/2"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-center">

          {/* Left: Text Content */}
          <Reveal>
            <div className="relative z-20">
              <div className="inline-block px-4 py-2 bg-white/5 text-blue-200 text-xs font-bold uppercase tracking-[0.2em] mb-8 border border-white/10">
                {t('shared.integration.tagline')}
              </div>

              <h2 className="text-4xl md:text-6xl font-bold text-white mb-8 leading-[1.15] tracking-tight uppercase">
                {t('shared.integration.title_line1')}<br />
                <span className="text-gray-500 block mt-2">{t('shared.integration.title_line2')}</span>
              </h2>

              <p className="text-gray-400 text-lg leading-relaxed mb-12 max-w-lg border-l-2 border-blue-500/30 pl-6">
                {t('shared.integration.subtitle')}
              </p>

              <div className="grid grid-cols-2 gap-y-6 gap-x-12">
                {(t('shared.integration.items', { returnObjects: true }) as { label: string, sub: string }[]).map((item, i) => (
                  <div key={i} className="flex flex-col">
                    <span className="text-white font-bold text-lg">{item.label}</span>
                    <span className="text-gray-600 text-sm font-mono uppercase tracking-wider">{item.sub}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Right: The X-Ray Scan Animation */}
          <Reveal delay={0.2}>
            <div className="flex flex-col items-center">
              <div className="w-full flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8 mb-8 relative">

                {/* CONNECTION LINE (The Wire) */}
                <div className="absolute top-1/2 left-10 right-10 h-px bg-blue-900/50 z-0 hidden md:block"></div>

                {/* === Left: Input Source (FLEX ITEM - Guaranteed Visibility) === */}
                <div className="flex flex-col items-center gap-4 relative z-20 order-1">
                  <div className="relative group">
                    <div className="w-16 h-16 bg-[#0F172A] border border-gray-700 rounded-xl flex items-center justify-center shadow-lg relative z-10">
                      <Files className="text-gray-400 group-hover:text-gray-200 transition-colors" size={24} />
                      {/* Mail Badge - Blue */}
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center border border-[#0F172A] shadow-md">
                        <Mail size={12} className="text-white" />
                      </div>
                    </div>
                  </div>
                  <div className="text-[10px] text-gray-500 font-mono uppercase tracking-widest text-center bg-rabbit-navy px-2 relative z-10">{t('shared.integration.visual.input')}</div>

                  {/* Dots pointing to scanner (Hidden on mobile to save vertical space) */}
                  <div className="hidden md:flex gap-1 absolute -right-8 top-1/2 -translate-y-1/2">
                    <div className="w-1 h-1 bg-blue-400/50 rounded-full animate-[flow_1.5s_infinite]"></div>
                    <div className="w-1 h-1 bg-blue-400/50 rounded-full animate-[flow_1.5s_infinite_0.2s]"></div>
                    <div className="w-1 h-1 bg-blue-400/50 rounded-full animate-[flow_1.5s_infinite_0.4s]"></div>
                  </div>
                </div>

                {/* === Center: The Scanner Container (FLEX ITEM) === */}
                <div className="relative w-56 h-72 bg-[#0F172A] border border-gray-700 rounded-lg shadow-2xl overflow-hidden z-30 group mx-auto order-2">

                  {/* LAYER 1: SCANNED / STRUCTURED (Underneath - Blue) */}
                  <div className="absolute inset-0 p-6 flex flex-col gap-4 z-0 bg-[#0F172A]">
                    <div className="flex items-center justify-between border-b border-blue-500/30 pb-4 mb-2">
                      <div className="h-3 w-24 bg-blue-500 rounded-sm shadow-[0_0_10px_rgba(59,130,246,0.5)]"></div>
                      <div className="h-2 w-8 bg-blue-900/50 rounded-sm"></div>
                    </div>
                    {/* Structured Grid */}
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="grid grid-cols-3 gap-2 opacity-80">
                        <div className="h-2 bg-blue-900/40 rounded-sm"></div>
                        <div className="h-2 bg-blue-900/40 rounded-sm"></div>
                        <div className="h-2 bg-blue-500/20 rounded-sm"></div>
                      </div>
                    ))}

                    <div className="mt-auto p-3 bg-blue-900/10 border border-blue-500/20 rounded flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-[10px] text-blue-300 font-mono tracking-wider">{t('shared.integration.visual.validated')}</span>
                    </div>
                  </div>

                  {/* LAYER 2: UNSCANNED / RAW (Top - Grey - Clipped by Scan) */}
                  <div className="absolute inset-0 p-6 flex flex-col gap-6 z-10 bg-[#0F172A] animate-scan-reveal origin-top">
                    <div className="w-full flex justify-between items-center opacity-50">
                      <FileText size={32} className="text-gray-500" />
                      <div className="h-8 w-8 bg-gray-800 rounded-full"></div>
                    </div>
                    <div className="space-y-3 opacity-40 blur-[0.5px]">
                      <div className="h-4 w-3/4 bg-gray-600 rounded-sm rotate-1"></div>
                      <div className="h-4 w-1/2 bg-gray-600 rounded-sm -rotate-1"></div>
                      <div className="h-20 w-full bg-gray-800/50 border border-dashed border-gray-600 rounded-sm mt-4"></div>
                      <div className="h-4 w-full bg-gray-600 rounded-sm"></div>
                    </div>
                  </div>

                  {/* === THE SCANNER BEAM === */}
                  <div className="absolute left-0 right-0 h-[2px] bg-cyan-400 shadow-[0_0_20px_rgba(34,211,238,1),0_0_10px_rgba(34,211,238,0.5)] z-20 animate-scan-move"></div>

                  {/* Reflection */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent pointer-events-none z-30"></div>
                </div>

                {/* === Right: Output Flow (FLEX ITEM) === */}
                <div className="flex flex-col items-center gap-4 relative z-20 order-3">
                  {/* Dots pointing from scanner */}
                  <div className="hidden md:flex gap-1 absolute -left-8 top-1/2 -translate-y-1/2">
                    <div className="w-1 h-1 bg-blue-500 rounded-full animate-[flow_1.5s_infinite]"></div>
                    <div className="w-1 h-1 bg-blue-500 rounded-full animate-[flow_1.5s_infinite_0.2s]"></div>
                    <div className="w-1 h-1 bg-blue-500 rounded-full animate-[flow_1.5s_infinite_0.4s]"></div>
                  </div>

                  <div className="relative">
                    <div className="w-16 h-16 bg-[#0F172A] border border-blue-500/30 rounded-xl flex items-center justify-center shadow-lg relative z-10">
                      <Database className="text-blue-400" size={24} />
                    </div>
                  </div>
                  <div className="text-[10px] text-gray-500 font-mono uppercase tracking-widest text-center bg-rabbit-navy px-2 relative z-10">{t('shared.integration.visual.system')}</div>
                </div>

              </div>

              {/* === TECHNICAL CAPTION === */}
              <div className="w-full max-w-md border-t border-gray-800 pt-6 mt-2 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-gray-500">
                <div className="flex items-center gap-2">
                  <Activity size={12} className="text-green-500 animate-pulse" />
                  <span>{t('shared.integration.caption.status')}</span>
                </div>
                <div className="hidden md:block">{t('shared.integration.caption.latency')}</div>
                <div className="flex items-center gap-2">
                  <ShieldCheck size={12} className="text-blue-500" />
                  <span>{t('shared.integration.caption.encrypted')}</span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      <style>{`
        @keyframes scan-move {
          0% { top: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes scan-reveal {
          0% { clip-path: inset(0 0 0 0); }
          100% { clip-path: inset(100% 0 0 0); }
        }
        @keyframes flow {
          0% { opacity: 0; transform: translateX(-5px); }
          50% { opacity: 1; }
          100% { opacity: 0; transform: translateX(5px); }
        }
        .animate-scan-move {
          animation: scan-move 4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
        }
        .animate-scan-reveal {
          animation: scan-reveal 4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
        }
      `}</style>
    </section>
  );
};
